import { isObjectLiteral, snake, transformKeys } from '~/lib/upfront';
import type { PaginatedApiResponse } from '~/types';

export default defineNuxtPlugin({
    name: 'fetch',
    setup: nuxtApp => {
        const config = useRuntimeConfig();
        let xsrfToken = useCookie('XSRF-TOKEN');

        const isPaginatedResponse = <T>(response: unknown): response is PaginatedApiResponse<T> => {
            return isObjectLiteral(response) && 'meta' in response && 'links' in response;
        };

        // One client per Nuxt app, i.e. per request on the server: its hooks forward this request's cookies and
        // visitor IP. Replacing globalThis.$fetch instead let concurrent SSR requests send each other's cookies.
        // 408 - Request Timeout
        // 409 - Conflict
        // 425 - Too Early (Experimental)
        // 429 - Too Many Requests
        // 504 - Gateway Timeout
        // 419 - CSRF Token Mismatch
        const retryStatusCodes = [408, 409, 425, 429, 504, 419];

        const api = $fetch.create({
            baseURL: '',
            retryStatusCodes,
            // always retry even if not GET request
            retry: 1,

            async onRequest({ options, request }) {
                if (request.toString().startsWith('/_nuxt')) return;

                const internal = import.meta.server && Boolean(config.apiInternalBase);
                const apiBase = internal ? config.apiInternalBase : config.public.apiBase;

                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                options.baseURL = options.baseURL || apiBase;

                // if not going to the api, don't do anything
                if (options.baseURL !== apiBase) return;

                options.credentials = 'include';

                // tree-shake
                if (import.meta.server) {
                    const allowedHeaders = [
                        'cookie', // Session cookies, required for auth
                        'referer', // For security checks and redirect back
                        'user-agent', // For logging and device detection
                        'accept-language', // Localization
                        'origin', // CORS and security
                        'cf-connecting-ip' // The visitor, so the API rate-limits them rather than this server
                    ];
                    // These describe this site, not the API; the API trusts them from the internal network and
                    // would generate links for (and host-check against) the wrong host.
                    const siteForwardingHeaders = [
                        'x-forwarded-host',
                        'x-forwarded-port',
                        'x-forwarded-prefix',
                        'x-forwarded-server'
                    ];
                    const requestHeaders = await nuxtApp.runWithContext(useRequestHeaders);
                    Object.entries(requestHeaders)
                        .forEach(([key, value]) => {
                            const keyLower = key.toLowerCase();

                            // we ignore everything we don't need, especially hop-by-hop headers
                            if (siteForwardingHeaders.includes(keyLower)) return;

                            if (keyLower.startsWith('x-') || allowedHeaders.includes(keyLower)) {
                                options.headers.set(key, value);
                            }
                        });

                    if (internal) {
                        // the internal hostname isn't one the API trusts; it serves under its public one
                        options.headers.set('x-forwarded-host', new URL(config.public.apiBase).host);
                        options.headers.set('x-forwarded-proto', 'https');
                    }
                }

                options.headers.set('accept', 'application/json');
                options.headers.set('accept-encoding', 'gzip, deflate, br');

                if (import.meta.server && !options.headers.has('origin')) {
                    // required to make the other server accept the session cookie
                    options.headers.set('origin', config.public.siteUrl);
                }

                const isRead = ['GET', 'HEAD', 'OPTIONS'].includes((options.method ?? 'GET').toUpperCase());

                if (isRead) {
                    // ofetch reports "no response" (dropped connection) as 500, which the list above leaves out, so a
                    // read lost in transit failed on its first attempt. This also retries a real 500 once; reads are
                    // idempotent. Writes keep the list above: the request may have been applied before the drop.
                    options.retryStatusCodes = [...retryStatusCodes, 500];
                } else {
                    if (!xsrfToken.value) {
                        await api('sanctum/csrf-cookie');
                        xsrfToken = useCookie('XSRF-TOKEN');
                    }

                    options.headers.set('X-XSRF-TOKEN', xsrfToken.value!);
                }

                if (options.body instanceof FormData) {
                    for (const [key, value] of options.body.entries()) {
                        options.body.delete(key);
                        options.body.set(snake(key), value);
                    }
                } else {
                    // not on a body-less read: a JSON Content-Type isn't CORS-safelisted, so the browser sent a
                    // preflight before every GET to the API
                    if (!isRead || options.body) {
                        options.headers.set('Content-Type', 'application/json; charset="utf-8"');
                    }

                    if (isObjectLiteral(options.body)) {
                        options.body = transformKeys(options.body, 'snake');
                    }
                }
            },

            parseResponse: body => {
                if (!body) return body;

                const parsed = JSON.parse(body);

                if (isPaginatedResponse(parsed)) {
                    return {
                        links: parsed.links,
                        meta: transformKeys(parsed.meta),
                        data: parsed.data.map(entry => {
                            return Array.isArray(entry)
                                ? entry.map(val => transformKeys(val as Record<string, any>))
                                : transformKeys(entry as Record<string, any>);
                        })
                    };
                }

                const data = isObjectLiteral(parsed) && 'data' in parsed ? parsed.data : parsed;

                if (isObjectLiteral(data) || Array.isArray(data)) {
                    return Array.isArray(data)
                        ? data.map(val => transformKeys(val as Record<string, any>))
                        : transformKeys(data);
                }

                return data;
            },

            onResponseError({ response, error }) {
                if (import.meta.server && import.meta.dev) {
                    // eslint-disable-next-line no-console
                    console.trace(error?.cause, error?.message);
                }

                if (response.status === 419) {
                    xsrfToken.value = undefined;
                }
            }
        });

        return { provide: { api } };
    }
});
